/**
 * TodaysFocus -- a single-CTA hero that surfaces the highest-value action
 * for the member's current state.
 *
 * Selection logic (first match wins):
 *   1. Phase 1 + no First Deal Declaration   -> "Sign your First Deal Declaration"
 *   2. Phase 1 complete, Phase 2 active      -> "Attend this week's Fast Track call"
 *   3. Phase 3 active + no completed CCC     -> "Run your Crystal Clear Criteria session"
 *   4. Phase 4 + Weekly 1 not submitted yet  -> "Submit this week's Weekly 1"
 *   5. Phase 4 + KPIs not logged this week   -> "Log this week's KPIs"
 *   6. Phase 5                               -> "Capture details on your first deal"
 *   7. fallback                              -> "Continue your journey"
 *
 * The card is intentionally a single suggestion. The phase-by-phase content
 * (cards, video, etc.) still renders below for members who want depth, but
 * the focus card answers the "what should I do today?" question first.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { Sparkles, ArrowRight, FileText, Video, Activity, Target, Trophy } from 'lucide-react';

type FocusItem = {
  icon: React.ElementType;
  eyebrow: string;
  title: string;
  description: string;
  ctaLabel: string;
  href: string;
};

const FALLBACK: FocusItem = {
  icon: Sparkles,
  eyebrow: "Today's focus",
  title: 'Continue your investing journey',
  description: 'Pick up where you left off. Your phase content is below.',
  ctaLabel: 'See my phase',
  href: '/dashboard',
};

export default function TodaysFocus() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [focus, setFocus] = useState<FocusItem | null>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const userId = user.id;
      const phase = user.phase ?? 1;

      // Pull a few state checks in parallel.
      const [progressR, ffdR, weeklyOneR] = await Promise.allSettled([
        supabase.from('member_progress').select('declaration_submitted, criteria_submitted').eq('user_id', userId).maybeSingle(),
        supabase.from('financial_freedom_declarations').select('id').eq('user_id', userId).maybeSingle(),
        supabase.from('weekly_ones').select('week_start_date').eq('user_id', userId).order('week_start_date', { ascending: false }).limit(1).maybeSingle(),
      ]);

      const progress = progressR.status === 'fulfilled' ? progressR.value.data : null;
      const ffd = ffdR.status === 'fulfilled' ? ffdR.value.data : null;
      const weeklyOne = weeklyOneR.status === 'fulfilled' ? weeklyOneR.value.data : null;

      const declarationDone = progress?.declaration_submitted === true || !!ffd;
      const today = new Date();
      const monday = (() => {
        const d = new Date(today);
        const day = d.getDay();
        d.setDate(d.getDate() - ((day + 6) % 7));
        d.setHours(0, 0, 0, 0);
        return d.toISOString().slice(0, 10);
      })();
      const submittedThisWeek = !!weeklyOne && weeklyOne.week_start_date === monday;

      let next: FocusItem;
      if (phase === 1 && !declarationDone) {
        next = {
          icon: FileText,
          eyebrow: 'Onboarding',
          title: 'Sign your First Deal Declaration',
          description: 'A short commitment statement to anchor your year. Two minutes.',
          ctaLabel: 'Open declaration',
          href: '/financial-freedom-declaration',
        };
      } else if (phase === 2) {
        next = {
          icon: Video,
          eyebrow: 'This week',
          title: 'Attend this week\'s Fast Track call',
          description: 'Join the live session for this week\'s lesson and discussion.',
          ctaLabel: 'See call details',
          href: '/dashboard',
        };
      } else if (phase === 3) {
        return;
      } else if (phase === 4 && !submittedThisWeek) {
        next = {
          icon: Activity,
          eyebrow: 'Weekly accountability',
          title: 'Submit this week\'s Weekly 1',
          description: 'A short reflection on last week and a commitment for this week. Two minutes.',
          ctaLabel: 'Open Weekly 1',
          href: '/dashboard',
        };
      } else if (phase === 4) {
        next = {
          icon: Target,
          eyebrow: 'Action phase',
          title: 'Log this week\'s KPIs',
          description: 'Track offers submitted, leads contacted, and analyses run this week.',
          ctaLabel: 'Open tracker',
          href: '/dashboard',
        };
      } else if (phase === 5) {
        next = {
          icon: Trophy,
          eyebrow: 'First Deal',
          title: 'Capture details on your first deal',
          description: 'You\'re here. Add the deal so we can celebrate it with you.',
          ctaLabel: 'Add first deal',
          href: '/dashboard',
        };
      } else {
        next = FALLBACK;
      }
      setFocus(next);
    })();
  }, [user]);

  if (!focus) return null;
  const Icon = focus.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
      className="mb-8 rounded-2xl border border-gold/30 bg-gradient-to-br from-amber-50/60 via-white to-amber-50/30 dark:from-amber-950/20 dark:via-background dark:to-amber-950/10 p-6 shadow-sm"
    >
      <div className="flex items-start gap-4">
        <div className="hidden sm:flex shrink-0 w-12 h-12 rounded-xl bg-gold/15 items-center justify-center">
          <Icon className="w-6 h-6 text-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold tracking-wider uppercase text-gold mb-1">
            {focus.eyebrow}
          </p>
          <h2 className="font-serif text-xl sm:text-2xl text-foreground leading-tight mb-1">
            {focus.title}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">{focus.description}</p>
          <button
            onClick={() => navigate(focus.href)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gold text-primary-foreground hover:bg-gold/90 transition-colors text-sm font-semibold active:scale-[0.97] transition-transform duration-150"
          >
            {focus.ctaLabel}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
